import {type PaymentData, type PaymentAttempt} from '../../../@shared/ts/paymentAttempt';
import {type TransactionUpdate} from '../../../@shared/ts/transaction';
import {type FeatureFlag} from '../../../@type/features';
import {insertCaptchaFields} from '../botProtection';
import {displayTokenizeError, submitOrder, unblockCheckout} from '../checkout';
import {Feature} from '../features';
import {createPaymentTransaction, updatePaymentTransaction} from '../transaction';

const getSelectedGatewayId = () => (jQuery('input[name="payment_method"]:checked').val() ?? '') as string;

function onPaymentMethodSelectedEvent() {
	let previousSelectedId = '';
	const callbacks: Record<string, (previousGatewayIdSelected: string) => void> = {};

	// jQuery is not loaded until the entire document is loaded
	document.addEventListener('DOMContentLoaded', () => {
		previousSelectedId = getSelectedGatewayId();
		jQuery(document.body).on('payment_method_selected', () => {
			const currentlySelectedId = getSelectedGatewayId();
			if (currentlySelectedId !== previousSelectedId) {
				callbacks?.[currentlySelectedId]?.(previousSelectedId);

				previousSelectedId = currentlySelectedId;
			}
		});
	});

	return (forGatewayId: string, callback: (previousGatewayIdSelected: string) => void) => {
		callbacks[forGatewayId] = callback;
	};
}

function onUpdatedCheckoutEvent() {
	const callbacks: Record<string, () => void> = {};

	// jQuery is not loaded until the entire document is loaded
	document.addEventListener('DOMContentLoaded', () => {
		const currentlySelectedId = getSelectedGatewayId();
		callbacks?.[currentlySelectedId]?.();

		jQuery(document.body).on('updated_checkout init_checkout', () => {
			const currentlySelectedId = getSelectedGatewayId();
			callbacks?.[currentlySelectedId]?.();
		});
	});

	return (forGatewayId: string, callback: () => void) => {
		callbacks[forGatewayId] = callback;
	};
}

function onCheckoutSubmitEvent() {
	const callbacks: Record<string, () => void> = {};

	document.addEventListener('click', e => {
		if (!(e.target as HTMLElement | null)?.closest('#place_order')) {
			return;
		}

		const currentlySelectedId = getSelectedGatewayId();
		const placeOrderClickCallback = callbacks[currentlySelectedId];
		if (placeOrderClickCallback) {
			e.preventDefault();
			placeOrderClickCallback();
		}
	});

	return (forGatewayId: string, callback: () => void) => {
		callbacks[forGatewayId] = callback;
	};
}

const registerGatewaySelectedHandler = onPaymentMethodSelectedEvent();
const registerUpdatedCheckoutHandler = onUpdatedCheckoutEvent();
const registerCheckoutSubmitHandler = onCheckoutSubmitEvent();

/**
 * Creates a payment attempt for the checkout page.
 */
function createNativeCheckoutPaymentAttempt(): PaymentAttempt {
	let transactionId: string | null;
	let paymentResult: PaymentData;

	return {
		getTransactionId() {
			if (transactionId) {
				return transactionId;
			}

			if (transactionId === null) {
				throw new Error('Transaction failed to be created.');
			} else {
				throw new Error('Transaction not created yet.');
			}
		},
		getOrderId() {
			if (paymentResult?.order_id) {
				return paymentResult.order_id;
			}

			throw new Error('Order not created yet.');
		},
		stopLoading: unblockCheckout,
		setPaymentMessage: displayTokenizeError,
		async submitOrder<T extends PaymentData>(dataType: string, extraFields: Record<string, string> = {}): Promise<T> {
			await insertCaptchaFields();

			paymentResult = await submitOrder<T>(dataType, {
				peachpay_transaction_id: transactionId!,
				...extraFields,
			});

			return paymentResult as T;
		},
		redirectCancel() {
			if (!paymentResult) {
				throw new Error('Payment result not set yet.');
			}

			window.top!.location.href = paymentResult.cancel_url;
		},
		redirectSuccess() {
			if (!paymentResult) {
				throw new Error('Payment result not set yet.');
			}

			window.top!.location.href = paymentResult.success_url;
		},

		async createTransaction(gatewayId: string) {
			transactionId = await createPaymentTransaction(gatewayId);
		},
		async completeTransaction(update: TransactionUpdate) {
			await updatePaymentTransaction(transactionId!, update);
		},

		featureEnabled(feature: FeatureFlag) {
			return Feature.enabled(feature);
		},
		featureMetadata<T = string>(feature: FeatureFlag, key: string) {
			const metadata = Feature.metadata<T>(feature, key);
			if (metadata === null) {
				throw new Error(`Feature metadata for '${feature}' with metadata key '${key}' does not exist`);
			}

			return metadata;
		},
	} satisfies PaymentAttempt;
}

export {
	registerGatewaySelectedHandler,
	registerUpdatedCheckoutHandler,
	registerCheckoutSubmitHandler,

	createNativeCheckoutPaymentAttempt,
};
